import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { StateFactory } from '@jct/ui';
import { AntiforgeryService, AccountService, IdentifyMethod, ResetPasswordModel } from '@jct/api';
import { Application, HttpStatusResult, WITHOUT_EMIT_EVENT } from '@jct/core';
import { applyIdentityKeyValidators, idNumberValidator } from '../../identity-form.utils';

@Component({
  selector: 'identify-form.page',
  templateUrl: './identify-form.html',
})
export class IdentifyForm implements OnInit {
  constructor(
    private sf: StateFactory,
    private fb: UntypedFormBuilder,
    private af: AntiforgeryService,
    private accountService: AccountService,
    private application: Application,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  loadState = this.sf.create();
  identifyState = this.sf.create();

  emailAddress: string;

  form = this.fb.group({
    key: ['', [Validators.required, idNumberValidator()]],
    method: [IdentifyMethod.IdNumber],
    passwordGeneratorRelativePath: [this.application.passwordGeneratorPath],
  });

  get method() {
    return this.form.get('method').value;
  }

  async ngOnInit() {
    await this.loadState.inProcess();
    let result = await this.af.generateAsync();

    if (!result.succeeded) {
      this.loadState.failed(result);
      return;
    }

    // Must run before the method subscription below, which clears the key on every change.
    this.applyIdentityFromQueryParams();

    this.form.get('method').valueChanges.subscribe(() => {
      const keyControl = this.form.get('key');
      keyControl.reset('', { emitEvent: false });
      keyControl.markAsPristine();
      keyControl.markAsUntouched();
      applyIdentityKeyValidators(keyControl, this.method);
      this.identifyState.clearError();
    });

    await this.loadState.completed();
  }

  private applyIdentityFromQueryParams() {
    const params = this.activatedRoute.snapshot.queryParams;
    const key = (params['key'] || '').toString();
    const method =
      params['method'] === IdentifyMethod.Passport
        ? IdentifyMethod.Passport
        : IdentifyMethod.IdNumber;

    if (!key && !params['method']) {
      return;
    }

    this.form.patchValue({ key, method });
    applyIdentityKeyValidators(this.form.get('key'), method);
  }

  async identify() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.identifyState.isInProcess) {
      this.form.disable(WITHOUT_EMIT_EVENT);
      await this.identifyState.inProcess();

      const model = this.form.getRawValue() as ResetPasswordModel;
      const result = await this.accountService.resetPassword(model);

      if (result instanceof HttpStatusResult) {
        this.form.enable(WITHOUT_EMIT_EVENT);
        this.identifyState.failed(result);
        return;
      }
      this.emailAddress = result;

      await this.router.navigate(['/reset-process/get-reset-link'], {
        queryParams: { emailAddress: this.emailAddress },
        skipLocationChange: true,
      });

      await this.identifyState.completed();
    }
  }
}
